/**
 * Utilitário para armazenar e recuperar dados de rastreamento usando IndexedDB
 */

const DB_NAME = 'rastreamento_db';
const DB_VERSION = 1;
const STORE_NAME = 'coordenadas_pendentes';
const CONFIG_STORE = 'config_rastreamento';

let dbInstance = null;

/**
 * Abrir conexão com IndexedDB
 */
function openDB() {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('❌ Erro ao abrir IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Store para coordenadas pendentes
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('carga_id', 'carga_id', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Store para configuração de rastreamento
      if (!db.objectStoreNames.contains(CONFIG_STORE)) {
        db.createObjectStore(CONFIG_STORE, { keyPath: 'key' });
      }
    };
  });
}

/**
 * Salvar coordenada pendente para envio posterior
 */
export async function savePendingCoordinate(coordinateData) {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const data = {
      carga_id: coordinateData.carga_id,
      latitude: coordinateData.latitude,
      longitude: coordinateData.longitude,
      motorista_id: coordinateData.motorista_id,
      motorista_nome: coordinateData.motorista_nome,
      motorista_telefone: coordinateData.motorista_telefone,
      timestamp: Date.now(),
      tentativas: 0,
    };

    await new Promise((resolve, reject) => {
      const request = store.add(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    console.log('💾 Coordenada salva no IndexedDB para envio posterior');
    return true;
  } catch (error) {
    console.error('❌ Erro ao salvar coordenada no IndexedDB:', error);
    return false;
  }
}

/**
 * Obter coordenadas pendentes
 */
export async function getPendingCoordinates(cargaId = null) {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = cargaId
        ? store.index('carga_id').getAll(cargaId)
        : store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('❌ Erro ao obter coordenadas pendentes:', error);
    return [];
  }
}

/**
 * Remover coordenada enviada com sucesso
 */
export async function removePendingCoordinate(id) {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    return true;
  } catch (error) {
    console.error('❌ Erro ao remover coordenada do IndexedDB:', error);
    return false;
  }
}

/**
 * Salvar configuração de rastreamento ativo
 */
export async function saveTrackingConfig(config) {
  try {
    const db = await openDB();
    const transaction = db.transaction([CONFIG_STORE], 'readwrite');
    const store = transaction.objectStore(CONFIG_STORE);

    await new Promise((resolve, reject) => {
      const request = store.put({ key: 'active_tracking', ...config });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // Também salvar no localStorage como fallback
    localStorage.setItem('tracking_config', JSON.stringify(config));
    return true;
  } catch (error) {
    console.error('❌ Erro ao salvar configuração de rastreamento:', error);
    // Fallback para localStorage
    try {
      localStorage.setItem('tracking_config', JSON.stringify(config));
      return true;
    } catch (e) {
      return false;
    }
  }
}

/**
 * Obter configuração de rastreamento ativo
 */
export async function getTrackingConfig() {
  try {
    const db = await openDB();
    const transaction = db.transaction([CONFIG_STORE], 'readonly');
    const store = transaction.objectStore(CONFIG_STORE);

    const data = await new Promise((resolve, reject) => {
      const request = store.get('active_tracking');
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (data) {
      const { key, ...config } = data;
      return config;
    }

    // Fallback para localStorage
    const localData = localStorage.getItem('tracking_config');
    return localData ? JSON.parse(localData) : null;
  } catch (error) {
    console.error('❌ Erro ao obter configuração de rastreamento:', error);
    // Fallback para localStorage
    try {
      const localData = localStorage.getItem('tracking_config');
      return localData ? JSON.parse(localData) : null;
    } catch (e) {
      return null;
    }
  }
}

/**
 * Limpar configuração de rastreamento
 */
export async function clearTrackingConfig() {
  try {
    const db = await openDB();
    const transaction = db.transaction([CONFIG_STORE], 'readwrite');
    const store = transaction.objectStore(CONFIG_STORE);

    await new Promise((resolve, reject) => {
      const request = store.delete('active_tracking');
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    localStorage.removeItem('tracking_config');
    return true;
  } catch (error) {
    console.error('❌ Erro ao limpar configuração de rastreamento:', error);
    localStorage.removeItem('tracking_config');
    return false;
  }
}

